import { useCallback, useEffect, useRef, useState } from 'react';
import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';

import { Button, ErrorText, Fieldset, Input, Layout, OverlayPopover, Pill } from '@utilities';

import { VALIDATION_MESSAGES } from '../../../../utilities/constants';

import styles from './_index.module.scss';

const ValuesBetween = ({ question, updateQuestionProperties }) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [propertiesToApply, setPropertiesToApply] = useState({ ...question.properties });

  const pillRef = useRef();
  const popoverRef = useRef(); // required to allow inputs inside the popover to be focused

  useEffect(() => {
    if (isPopoverOpen) {
      setPropertiesToApply({ ...question.properties });
    }
  }, [isPopoverOpen]);

  const handleApply = useCallback(() => {
    updateQuestionProperties({ ...propertiesToApply });
    setIsPopoverOpen(false);
  }, [propertiesToApply, updateQuestionProperties]);

  const isApplyDisabled =
    isNaN(propertiesToApply.minNumber) ||
    isNaN(propertiesToApply.maxNumber) ||
    propertiesToApply.maxNumber <= propertiesToApply.minNumber ||
    propertiesToApply.maxNumber > question.properties.maxTotal ||
    (question.properties.forcePercent && propertiesToApply.maxNumber > 100) ||
    isEqual(question.properties, propertiesToApply);

  return (
    <>
      <Layout.Flex className={styles['values-between']} gap="x-small">
        <span ref={popoverRef}>Allow Values Between</span>
        <Pill
          buttonRef={pillRef}
          isModified={!!question.properties?.maxAnswers}
          onClick={() => setIsPopoverOpen(!isPopoverOpen)}
          value={`${question.properties.minNumber} and ${question.properties.maxNumber}`}
        />
      </Layout.Flex>
      <OverlayPopover
        className={styles['values-between-popover']}
        container={popoverRef.current}
        hasArrow
        isActive={isPopoverOpen}
        onHide={() => setIsPopoverOpen(false)}
        target={pillRef.current}
      >
        <>
          <Fieldset legend="Allow Values Between">
            <Layout.Flex className={styles['values-between-row']} gap="small">
              <Input
                id="min-number-input"
                min={0}
                onChange={(value) =>
                  setPropertiesToApply((prev) => ({ ...prev, minNumber: parseFloat(value) }))
                }
                type="number"
                value={propertiesToApply.minNumber}
              />
              <Input
                id="max-number-input"
                max={question.properties.maxTotal}
                min={propertiesToApply.minNumber + 1}
                onChange={(value) =>
                  setPropertiesToApply((prev) => ({ ...prev, maxNumber: parseFloat(value) }))
                }
                type="number"
                value={propertiesToApply.maxNumber}
              />
            </Layout.Flex>
            {propertiesToApply.maxNumber <= propertiesToApply.minNumber && (
              <ErrorText
                className={styles['values-between-error']}
                text="Max value must be larger than min value."
              />
            )}
            {propertiesToApply.maxNumber > question.properties.maxTotal && (
              <ErrorText
                className={styles['values-between-error']}
                text={VALIDATION_MESSAGES.MAX_VALUE_MAX_TOTAL_ERROR}
              />
            )}
            {question.properties.forcePercent && propertiesToApply.maxNumber > 100 && (
              <ErrorText
                className={styles['values-between-error']}
                text={VALIDATION_MESSAGES.MAX_PERCENTAGE_ERROR}
              />
            )}
          </Fieldset>
          <Button isDisabled={isApplyDisabled} onClick={handleApply} text="Apply" />
        </>
      </OverlayPopover>
    </>
  );
};

ValuesBetween.propTypes = {
  question: PropTypes.object.isRequired,
  updateQuestionProperties: PropTypes.func.isRequired,
};

export default ValuesBetween;
